import React from "react"
import { useQuery } from "@tanstack/react-query"
import { Star, MessageSquareText } from "lucide-react"
import api from "../../services/api"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card"
import { Progress } from "../ui/progress"
import { Skeleton } from "../ui/skeleton"

// Star rating display
function StarDisplay({ rating }) {
  const fullStars = Math.floor(rating)
  const hasHalfStar = rating % 1 >= 0.5
  
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <Star
          key={star}
          className={`h-5 w-5 ${
            star <= fullStars
              ? "text-yellow-500 fill-yellow-500"
              : star === fullStars + 1 && hasHalfStar
              ? "text-yellow-500 fill-yellow-500/50"
              : "text-muted-foreground"
          }`}
        />
      ))}
    </div>
  )
}

// Rating bar row
function RatingBar({ stars, count, total }) {
  const percentage = total > 0 ? (count / total) * 100 : 0
  
  return (
    <div className="flex items-center gap-2">
      <div className="flex items-center gap-1 w-8 justify-end">
        <span className="text-sm text-muted-foreground">{stars}</span>
        <Star className="h-3 w-3 text-yellow-500 fill-yellow-500" />
      </div>
      <Progress value={percentage} className="h-2 flex-1" />
      <span className="text-sm text-muted-foreground w-10 text-right">
        {count}
      </span>
    </div>
  )
}

export default function RatingSummary() {
  const { data, isLoading, error } = useQuery({
    queryKey: ["appstore-reviews", false], // Always get all reviews for stats
    queryFn: () => api.getAppStoreReviews(false),
    staleTime: 5 * 60 * 1000,
    retry: 1,
  })

  const totalCount = data?.totalCount || 0
  const averageRating = data?.averageRating || 0
  const distribution = data?.ratingDistribution || { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 }
  const responseRate = data?.responseRate || 0

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <Star className="h-5 w-5 text-muted-foreground" />
          <div>
            <CardTitle>Rating Summary</CardTitle>
            <CardDescription>
              Overall app rating on the App Store
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="space-y-4">
            <Skeleton className="h-16 w-full" />
            <div className="space-y-2">
              {[5, 4, 3, 2, 1].map((i) => (
                <Skeleton key={i} className="h-4 w-full" />
              ))}
            </div>
          </div>
        ) : error ? (
          <div className="h-[200px] flex items-center justify-center text-muted-foreground">
            <p>Failed to load rating data</p>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Average Rating */}
            <div className="flex items-center gap-4">
              <div>
                <div className="text-4xl font-bold">
                  {averageRating > 0 ? averageRating.toFixed(1) : "—"}
                </div>
                <div className="text-sm text-muted-foreground">
                  out of 5
                </div>
              </div>
              <div className="flex flex-col gap-1">
                <StarDisplay rating={averageRating} />
                <div className="text-sm text-muted-foreground">
                  {totalCount.toLocaleString()} {totalCount === 1 ? "review" : "reviews"}
                </div>
              </div>
            </div>

            {/* Rating Distribution */}
            <div className="space-y-2">
              {[5, 4, 3, 2, 1].map((stars) => (
                <RatingBar
                  key={stars}
                  stars={stars}
                  count={distribution[stars] || 0}
                  total={totalCount}
                />
              ))}
            </div>

            {/* Response Rate */}
            <div className="pt-4 border-t">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <MessageSquareText className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">Response Rate</span>
                </div>
                <span className="text-sm font-medium">
                  {parseFloat(responseRate).toFixed(0)}%
                </span>
              </div>
              <Progress value={parseFloat(responseRate)} className="h-2 mt-2" />
              <p className="text-xs text-muted-foreground mt-1">
                {totalCount - (data?.unrepliedCount || 0)} of {totalCount} reviews answered
              </p>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
